import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useTheme } from "@/context/ThemeContext";
import { LANGUAGES } from "@/i18n";
import { Palette, Translate, Sun, Moon } from "@phosphor-icons/react";

export default function Settings() {
  const { t, i18n } = useTranslation();
  const { mode, setMode, accent, setAccent, accents } = useTheme();
  const [lang, setLang] = useState(i18n.language || "en");

  useEffect(() => {
    i18n.changeLanguage(lang);
    localStorage.setItem("hk_lang", lang);
  }, [lang, i18n]);

  return (
    <div className="space-y-6 max-w-3xl" data-testid="settings-page">
      <header>
        <div className="label-small text-blue-700">{t("preferences")}</div>
        <h1 className="text-3xl md:text-4xl font-extrabold tracking-tight text-slate-900 mt-1" style={{ fontFamily: "Manrope" }}>{t("settings")}</h1>
        <p className="text-sm text-slate-600 mt-1">{t("appearance")} & {t("language").toLowerCase()}.</p>
      </header>

      <section className="flat-card p-6">
        <div className="flex items-center gap-2 mb-3"><Translate size={18} className="text-slate-500" /><div className="label-small">{t("language")}</div></div>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
          {LANGUAGES.map((l) => (
            <button
              key={l.code}
              data-testid={`lang-${l.code}`}
              onClick={() => setLang(l.code)}
              className={`px-3 py-2 border rounded-md text-sm font-semibold transition-colors text-left ${
                lang === l.code
                  ? "bg-blue-600 border-blue-600 text-white"
                  : "bg-white border-slate-200 hover:border-blue-300 text-slate-700"
              }`}
            >
              <div>{l.label}</div>
              <div className={`text-[10px] uppercase tracking-widest font-semibold mt-0.5 ${lang === l.code ? "text-blue-100" : "text-slate-400"}`}>{l.code}</div>
            </button>
          ))}
        </div>
      </section>

      <section className="flat-card p-6">
        <div className="flex items-center gap-2 mb-3"><Sun size={18} className="text-slate-500" /><div className="label-small">{t("theme")}</div></div>
        <div className="flex gap-2">
          <button data-testid="theme-light" onClick={() => setMode("light")} className={`px-4 py-2 border rounded-md text-sm font-semibold inline-flex items-center gap-2 transition-colors ${mode === "light" ? "bg-blue-600 border-blue-600 text-white" : "bg-white border-slate-200 text-slate-700 hover:border-blue-300"}`}>
            <Sun size={14} /> {t("light")}
          </button>
          <button data-testid="theme-dark" onClick={() => setMode("dark")} className={`px-4 py-2 border rounded-md text-sm font-semibold inline-flex items-center gap-2 transition-colors ${mode === "dark" ? "bg-blue-600 border-blue-600 text-white" : "bg-white border-slate-200 text-slate-700 hover:border-blue-300"}`}>
            <Moon size={14} /> {t("dark")}
          </button>
        </div>
      </section>

      <section className="flat-card p-6">
        <div className="flex items-center gap-2 mb-3"><Palette size={18} className="text-slate-500" /><div className="label-small">{t("accent_color")}</div></div>
        <div className="flex flex-wrap gap-2">
          {accents.map((a) => (
            <button
              key={a}
              data-testid={`accent-${a}`}
              onClick={() => setAccent(a)}
              className={`relative w-12 h-12 rounded-md border-2 transition-all ${accent === a ? "border-slate-900 scale-110" : "border-slate-200 hover:border-slate-400"}`}
              style={{ background: ({
                blue: "#2563EB", green: "#16A34A", purple: "#7C3AED",
                orange: "#EA580C", slate: "#475569", rose: "#E11D48",
              })[a] }}
              title={a}
            />
          ))}
        </div>
      </section>
    </div>
  );
}
